/**
 * Root finding for f(x) = 0 by Newton's method, bisection and the secant method
 * (Burden & Faires, Numerical Analysis, §2.1–2.3), each returning its full iteration
 * table so a student can follow the arithmetic.
 *
 * Newton:     xₙ₊₁ = xₙ − f(xₙ)/f′(xₙ)             order 2 at a simple root
 * Secant:     xₙ₊₁ = xₙ − f(xₙ)(xₙ − xₙ₋₁)/(f(xₙ) − f(xₙ₋₁))   order (1+√5)/2 ≈ 1.618
 * Bisection:  halve a bracket [a, b] with f(a)f(b) < 0   one bit per step
 *
 * Every run says why it stopped: converged, or the derivative vanished, the iterate left
 * the real domain, it ran away, it fell into a cycle, or it hit the iteration limit.
 */

export type Curve = (x: number) => number;

export type StopReason =
  | "converged"
  | "zero-derivative"
  | "undefined"
  | "diverged"
  | "cycle"
  | "max-iterations"
  | "no-bracket"
  | "flat-secant";

export interface RootOptions {
  /** Stop when the step (or bracket width) is below tolerance·max(1, |x|). */
  tolerance?: number;
  maxIterations?: number;
}

export interface NewtonRow {
  n: number;
  x: number;
  fx: number;
  dfx: number;
  /** xₙ₊₁ */
  next: number;
  step: number;
}

export interface SecantRow {
  n: number;
  previous: number;
  x: number;
  fPrevious: number;
  fx: number;
  next: number;
  step: number;
}

export interface BisectionRow {
  n: number;
  a: number;
  b: number;
  mid: number;
  fMid: number;
  width: number;
}

export interface RootRun<Row> {
  rows: Row[];
  reason: StopReason;
  /** Best estimate of the root (the last iterate), NaN if the run never got started. */
  root: number;
  /** |f(root)|. */
  residual: number;
}

const DEFAULTS = { tolerance: 1e-10, maxIterations: 50 };
const RUNAWAY = 1e8;
const TINY_DERIVATIVE = 1e-14;

function settings(options: RootOptions) {
  return { ...DEFAULTS, ...options };
}

function cycling(history: number[], tolerance: number): boolean {
  const n = history.length;
  if (n < 5) {
    return false;
  }
  const scale = Math.max(1, Math.abs(history[n - 1]));
  return (
    Math.abs(history[n - 1] - history[n - 3]) < 1e-9 * scale &&
    Math.abs(history[n - 3] - history[n - 5]) < 1e-9 * scale &&
    Math.abs(history[n - 1] - history[n - 2]) > tolerance * scale
  );
}

export function newton(
  f: Curve,
  df: Curve,
  start: number,
  options: RootOptions = {}
): RootRun<NewtonRow> {
  const { tolerance, maxIterations } = settings(options);
  const rows: NewtonRow[] = [];
  const history = [start];
  let x = start;
  for (let n = 0; n < maxIterations; n += 1) {
    const fx = f(x);
    const dfx = df(x);
    if (!(Number.isFinite(fx) && Number.isFinite(dfx))) {
      rows.push({ n, x, fx, dfx, next: Number.NaN, step: Number.NaN });
      return { rows, reason: "undefined", root: x, residual: Math.abs(fx) };
    }
    if (fx === 0) {
      rows.push({ n, x, fx, dfx, next: x, step: 0 });
      return { rows, reason: "converged", root: x, residual: 0 };
    }
    if (Math.abs(dfx) < TINY_DERIVATIVE) {
      rows.push({ n, x, fx, dfx, next: Number.NaN, step: Number.NaN });
      return {
        rows,
        reason: "zero-derivative",
        root: x,
        residual: Math.abs(fx),
      };
    }
    const step = -fx / dfx;
    const next = x + step;
    rows.push({ n, x, fx, dfx, next, step });
    history.push(next);
    x = next;
    if (Math.abs(step) <= tolerance * Math.max(1, Math.abs(x))) {
      return { rows, reason: "converged", root: x, residual: Math.abs(f(x)) };
    }
    if (Math.abs(x) > RUNAWAY) {
      return { rows, reason: "diverged", root: x, residual: Math.abs(f(x)) };
    }
    if (cycling(history, tolerance)) {
      return { rows, reason: "cycle", root: x, residual: Math.abs(f(x)) };
    }
  }
  return { rows, reason: "max-iterations", root: x, residual: Math.abs(f(x)) };
}

export function secant(
  f: Curve,
  first: number,
  second: number,
  options: RootOptions = {}
): RootRun<SecantRow> {
  const { tolerance, maxIterations } = settings(options);
  const rows: SecantRow[] = [];
  const history = [first, second];
  let previous = first;
  let x = second;
  let fPrevious = f(previous);
  for (let n = 0; n < maxIterations; n += 1) {
    const fx = f(x);
    if (!(Number.isFinite(fx) && Number.isFinite(fPrevious))) {
      rows.push({
        n,
        previous,
        x,
        fPrevious,
        fx,
        next: Number.NaN,
        step: Number.NaN,
      });
      return { rows, reason: "undefined", root: x, residual: Math.abs(fx) };
    }
    if (fx === 0) {
      rows.push({ n, previous, x, fPrevious, fx, next: x, step: 0 });
      return { rows, reason: "converged", root: x, residual: 0 };
    }
    if (fx === fPrevious) {
      rows.push({
        n,
        previous,
        x,
        fPrevious,
        fx,
        next: Number.NaN,
        step: Number.NaN,
      });
      return { rows, reason: "flat-secant", root: x, residual: Math.abs(fx) };
    }
    const step = (-fx * (x - previous)) / (fx - fPrevious);
    const next = x + step;
    rows.push({ n, previous, x, fPrevious, fx, next, step });
    history.push(next);
    previous = x;
    fPrevious = fx;
    x = next;
    if (Math.abs(step) <= tolerance * Math.max(1, Math.abs(x))) {
      return { rows, reason: "converged", root: x, residual: Math.abs(f(x)) };
    }
    if (Math.abs(x) > RUNAWAY) {
      return { rows, reason: "diverged", root: x, residual: Math.abs(f(x)) };
    }
    if (cycling(history, tolerance)) {
      return { rows, reason: "cycle", root: x, residual: Math.abs(f(x)) };
    }
  }
  return { rows, reason: "max-iterations", root: x, residual: Math.abs(f(x)) };
}

export function bisection(
  f: Curve,
  left: number,
  right: number,
  options: RootOptions = {}
): RootRun<BisectionRow> {
  const { tolerance } = settings(options);
  // Bisection halves every step, so allow enough steps to reach the tolerance.
  const maxIterations = options.maxIterations ?? 100;
  const rows: BisectionRow[] = [];
  let a = Math.min(left, right);
  let b = Math.max(left, right);
  let fa = f(a);
  const fb = f(b);
  if (!(Number.isFinite(fa) && Number.isFinite(fb))) {
    return {
      rows,
      reason: "undefined",
      root: Number.NaN,
      residual: Number.NaN,
    };
  }
  if (fa === 0) {
    return { rows, reason: "converged", root: a, residual: 0 };
  }
  if (fb === 0) {
    return { rows, reason: "converged", root: b, residual: 0 };
  }
  if (Math.sign(fa) === Math.sign(fb)) {
    return {
      rows,
      reason: "no-bracket",
      root: Number.NaN,
      residual: Number.NaN,
    };
  }
  for (let n = 0; n < maxIterations; n += 1) {
    const mid = (a + b) / 2;
    const fMid = f(mid);
    rows.push({ n, a, b, mid, fMid, width: b - a });
    if (!Number.isFinite(fMid)) {
      return { rows, reason: "undefined", root: mid, residual: Math.abs(fMid) };
    }
    if (fMid === 0 || (b - a) / 2 <= tolerance * Math.max(1, Math.abs(mid))) {
      return { rows, reason: "converged", root: mid, residual: Math.abs(fMid) };
    }
    if (Math.sign(fMid) === Math.sign(fa)) {
      a = mid;
      fa = fMid;
    } else {
      b = mid;
    }
  }
  const mid = (a + b) / 2;
  return {
    rows,
    reason: "max-iterations",
    root: mid,
    residual: Math.abs(f(mid)),
  };
}

/**
 * The sign change nearest `centre` within ± `radius`: scans outward in both directions
 * and returns the first bracket [x, x + step] where f changes sign, or null.
 */
export function findBracket(
  f: Curve,
  centre: number,
  radius = 10,
  steps = 400
): [number, number] | null {
  const step = radius / steps;
  for (let i = 0; i < steps; i += 1) {
    for (const side of [1, -1]) {
      const lo = side === 1 ? centre + i * step : centre - (i + 1) * step;
      const hi = lo + step;
      const [flo, fhi] = [f(lo), f(hi)];
      if (
        Number.isFinite(flo) &&
        Number.isFinite(fhi) &&
        (flo === 0 || fhi === 0 || Math.sign(flo) !== Math.sign(fhi))
      ) {
        return [lo, hi];
      }
    }
  }
  return null;
}

export interface ConvergenceNotes {
  /** Observed order of convergence from the last few errors, or null if not measurable. */
  order: number | null;
  /** Observed error ratio eₙ₊₁/eₙ when convergence looks linear. */
  ratio: number | null;
  /** 1/(1 − ratio) when a linear ratio near ½, ⅔ … points to a root of that multiplicity. */
  multiplicity: number | null;
}

/**
 * Order and linear ratio from the iterates. The sizes of successive steps |xₙ₊₁ − xₙ|
 * stand in for the unknown errors (they are proportional to them), so no true root is
 * needed: order p ≈ ln(s₂/s₁)/ln(s₁/s₀) over the last three steps, and ratio s₂/s₁.
 */
export function convergenceNotes(iterates: number[]): ConvergenceNotes {
  const scale = Math.max(1, Math.abs(iterates.at(-1) ?? 1));
  const steps: number[] = [];
  for (let i = 1; i < iterates.length; i += 1) {
    const step = Math.abs(iterates[i] - iterates[i - 1]);
    if (step > 1e-13 * scale) {
      steps.push(step);
    }
  }
  if (steps.length < 3) {
    return { order: null, ratio: null, multiplicity: null };
  }
  const [s0, s1, s2] = steps.slice(-3);
  const order = Math.log(s2 / s1) / Math.log(s1 / s0);
  const ratio = s2 / s1;
  const linear =
    Number.isFinite(order) && order < 1.3 && ratio > 0.1 && ratio < 0.95;
  const m = 1 / (1 - ratio);
  const multiplicity =
    linear && Math.abs(m - Math.round(m)) < 0.2 && Math.round(m) >= 2
      ? Math.round(m)
      : null;
  return {
    order: Number.isFinite(order) ? order : null,
    ratio: Number.isFinite(ratio) ? ratio : null,
    multiplicity,
  };
}
